import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MapPin, Search } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { GeoPoint } from '@aeris/contracts'
import {
  Badge,
  Button,
  EmptyState,
  Field,
  InlineAlert,
  Input,
  ListToolbar,
  Page,
  PageHeader,
  QueryState,
  ResultCount,
  SearchInput,
  Section,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  notify,
} from '@aeris/ui'

import { AddressInputs } from '../components/address-inputs.tsx'
import { CouriersEditor } from '../components/couriers-editor.tsx'
import { LocateCustomerDialog } from '../components/locate-customer-dialog.tsx'
import { MapView } from '../components/map-view.tsx'
import { RoutePlanner } from '../components/route-planner.tsx'
import { RoutesOnStreet } from '../components/routes-on-street.tsx'
import {
  EMPTY_ADDRESS,
  ufOrNull,
  type AddressValues,
} from '../lib/address-values.ts'
import {
  ApiError,
  fetchDeliveryOrigin,
  fetchUnlocatedCustomers,
  geocodeAddress,
  saveDeliveryOrigin,
  type DeliveryOriginView,
  type GeocodeCandidateView,
  type UnlocatedCustomerView,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { shopNameOf } from '../lib/shop-name.ts'

/**
 * Rotas de entrega (F265–F267), atrás da chave `entregas-rota`.
 *
 * Nesta fatia (F265) a tela prepara o terreno da rota:
 *
 * - **Clientes sem ponto** — quem recebeu entrega nos últimos 60 dias e não
 *   tem endereço com ponto no mapa, quem mais recebe primeiro. Na Elite eram
 *   66 dos 85 lojistas, quase todos sem endereço nenhum.
 * - **Saída da loja** — de onde as entregas desta filial saem, e até onde a
 *   entrega vai a pé.
 */

const dayOf = (iso: string): string =>
  new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
  }).format(new Date(iso))

const addressLine = (customer: UnlocatedCustomerView): string | null => {
  const main = customer.addresses[0]
  if (main === undefined) return null
  return [
    [main.street, main.number].filter(Boolean).join(', '),
    main.district,
    `${main.city}/${main.uf}`,
  ]
    .filter(Boolean)
    .join(' — ')
}

const errorText = (cause: unknown): string =>
  cause instanceof ApiError ? cause.message : 'Não foi possível agora.'

/** Quem recebeu entrega e ainda não tem ponto. */
const UnlocatedTab = ({ canLocate }: { canLocate: boolean }): ReactNode => {
  const queryClient = useQueryClient()
  const [locating, setLocating] = useState<UnlocatedCustomerView | null>(null)
  const [search, setSearch] = useState('')
  const customers = useQuery({
    queryKey: ['delivery-routes', 'unlocated'],
    queryFn: async ({ signal }) => await fetchUnlocatedCustomers(signal),
  })
  const all = customers.data ?? []
  const needle = search.trim().toLocaleLowerCase('pt-BR')
  const rows =
    needle === ''
      ? all
      : all.filter((customer) =>
          `${customer.legalName} ${customer.tradeName ?? ''}`
            .toLocaleLowerCase('pt-BR')
            .includes(needle)
        )

  return (
    <Section
      title="Clientes sem ponto no mapa"
      description="Quem recebeu entrega nos últimos 60 dias e ainda não tem ponto. Sem ponto, o cliente não entra na rota. Comece por quem mais recebe."
    >
      <ListToolbar>
        <SearchInput
          aria-label="Buscar cliente sem ponto"
          placeholder="Buscar pelo nome"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value)
          }}
        />
        {customers.data === undefined ? null : (
          <ResultCount
            page={1}
            pageSize={Math.max(rows.length, 1)}
            total={rows.length}
            none="Nenhum cliente encontrado"
          />
        )}
      </ListToolbar>
      <QueryState
        isPending={customers.isPending}
        isError={customers.isError}
        error={customers.error}
        onRetry={() => {
          void customers.refetch()
        }}
        what="os clientes sem ponto"
        isEmpty={rows.length === 0}
        filtered={needle !== ''}
        onClearFilters={() => {
          setSearch('')
        }}
        empty={
          <EmptyState
            icon={<MapPin aria-hidden="true" className="h-8 w-8" />}
            title="Todo mundo que recebe entrega já tem ponto"
            description="Quando alguém novo receber entrega sem ponto, ele aparece aqui."
          />
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Cliente</TableHeaderCell>
                <TableHeaderCell className="text-right">
                  Entregas
                </TableHeaderCell>
                <TableHeaderCell>Última</TableHeaderCell>
                <TableHeaderCell>Endereço</TableHeaderCell>
                <TableHeaderCell>
                  <span className="sr-only">Ação</span>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((customer) => {
                const line = addressLine(customer)
                return (
                  <TableRow key={customer.partyId}>
                    <TableCell>
                      <span className="font-medium">
                        {shopNameOf(customer)}
                      </span>
                      <span className="block text-(length:--text-xs) text-(--text-muted)">
                        {customer.legalName}
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {customer.deliveries}
                    </TableCell>
                    <TableCell>{dayOf(customer.lastDeliveryAt)}</TableCell>
                    <TableCell>
                      {line === null ? (
                        <Badge tone="warning">sem endereço</Badge>
                      ) : (
                        <span className="text-(length:--text-sm)">{line}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {canLocate ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setLocating(customer)
                          }}
                        >
                          <MapPin aria-hidden="true" className="h-4 w-4" />
                          Localizar
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </QueryState>

      {locating === null ? null : (
        <LocateCustomerDialog
          key={locating.partyId}
          customer={locating}
          open
          onOpenChange={(open) => {
            if (!open) setLocating(null)
          }}
          onLocated={() => {
            notify.success(`${shopNameOf(locating)} está no mapa.`)
            void queryClient.invalidateQueries({
              queryKey: ['delivery-routes', 'unlocated'],
            })
          }}
        />
      )}
    </Section>
  )
}

/** De onde as entregas desta filial saem. */
const OriginTab = ({
  origin,
  canEdit,
}: {
  origin: DeliveryOriginView
  canEdit: boolean
}): ReactNode => {
  const queryClient = useQueryClient()
  const saved = origin.origin
  const [address, setAddress] = useState<AddressValues>({
    ...EMPTY_ADDRESS,
    uf: 'MG',
  })
  const [candidate, setCandidate] = useState<GeocodeCandidateView | null>(null)
  const [pin, setPin] = useState<GeoPoint | null>(null)
  const [radius, setRadius] = useState(String(saved?.footRadiusM ?? 1000))
  const [error, setError] = useState<string | null>(null)

  const find = useMutation({
    mutationFn: async () => {
      const uf = ufOrNull(address.uf)
      if (
        address.street.trim() === '' ||
        address.city.trim() === '' ||
        uf === null
      ) {
        throw new Error('Preencha rua e cidade da loja.')
      }
      return await geocodeAddress({
        street: address.street,
        number: address.number,
        district: address.district,
        city: address.city,
        uf,
        cep: address.cep,
      })
    },
    onMutate: () => {
      setError(null)
    },
    onSuccess: (found) => {
      const first = found[0] ?? null
      setCandidate(first)
      setPin(
        first === null
          ? null
          : { latitude: first.latitude, longitude: first.longitude }
      )
      if (first === null)
        setError('Não achei este endereço no mapa. Confira rua e número.')
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof Error && !(cause instanceof ApiError)
          ? cause.message
          : errorText(cause)
      )
    },
  })

  const save = useMutation({
    mutationFn: async (input: { point: GeoPoint; label: string }) => {
      const footRadiusM = Number(radius)
      if (
        !Number.isInteger(footRadiusM) ||
        footRadiusM < 100 ||
        footRadiusM > 5000
      ) {
        throw new Error('A distância a pé vai de 100 a 5.000 metros.')
      }
      return await saveDeliveryOrigin({
        addressLabel: input.label,
        point: input.point,
        footRadiusM,
        version: saved?.version ?? null,
      })
    },
    onSuccess: async () => {
      setCandidate(null)
      setPin(null)
      notify.success('A saída da loja foi gravada.')
      await queryClient.invalidateQueries({
        queryKey: ['delivery-routes', 'origin'],
      })
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof Error && !(cause instanceof ApiError)
          ? cause.message
          : errorText(cause)
      )
    },
  })

  if (origin.branchId === null) {
    return (
      <InlineAlert tone="warning">
        Escolha a filial em que você está para dizer de onde as entregas saem.
      </InlineAlert>
    )
  }

  return (
    <Section
      title="Saída da loja"
      description="De onde os motoboys saem e para onde voltam. A rota começa e termina aqui."
    >
      {saved === null ? (
        <InlineAlert tone="info">
          Esta filial ainda não disse de onde as entregas saem.
        </InlineAlert>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="text-(length:--text-sm)">
            <span className="font-medium">Sai de:</span> {saved.addressLabel}
          </p>
          <p className="text-(length:--text-sm) text-(--text-secondary)">
            Até {saved.footRadiusM.toLocaleString('pt-BR')} m andando, a entrega
            vai a pé.
          </p>
          {pin === null ? (
            <MapView
              label="A saída da loja"
              center={saved}
              markers={[
                {
                  id: 'saida',
                  point: saved,
                  label: 'S',
                  title: 'Saída da loja',
                },
              ]}
            />
          ) : null}
        </div>
      )}

      {canEdit ? (
        <div className="flex flex-col gap-3">
          <p className="text-(length:--text-sm) font-medium">
            {saved === null ? 'Endereço da loja' : 'Mudar a saída'}
          </p>
          <AddressInputs values={address} onChange={setAddress} />
          <div className="flex flex-wrap items-end gap-3">
            <Field label="A pé até (metros)" className="w-40">
              {(field) => (
                <Input
                  {...field}
                  inputMode="numeric"
                  value={radius}
                  onChange={(event) => {
                    setRadius(event.target.value.replace(/\D/g, ''))
                  }}
                />
              )}
            </Field>
            <Button
              variant="secondary"
              disabled={find.isPending}
              onClick={() => {
                find.mutate()
              }}
            >
              <Search aria-hidden="true" className="h-4 w-4" />
              Achar no mapa
            </Button>
            {saved !== null && pin === null ? (
              <Button
                disabled={save.isPending}
                onClick={() => {
                  save.mutate({ point: saved, label: saved.addressLabel })
                }}
              >
                Salvar só a distância
              </Button>
            ) : null}
          </div>
          {find.isPending ? (
            <p className="flex items-center gap-2 text-(length:--text-sm)">
              <Spinner /> Buscando no mapa…
            </p>
          ) : null}
          {candidate !== null && pin !== null ? (
            <div className="flex flex-col gap-2">
              <p className="text-(length:--text-sm)">
                {candidate.formattedAddress}
              </p>
              <MapView
                label="Conferir a saída da loja"
                center={pin}
                zoom={17}
                pin={{ point: pin, onMove: setPin }}
              />
              <p className="text-(length:--text-xs) text-(--text-muted)">
                Confira se o pino está na porta da loja. Arraste se não estiver.
              </p>
              <div>
                <Button
                  disabled={save.isPending}
                  onClick={() => {
                    save.mutate({
                      point: pin,
                      label: candidate.formattedAddress,
                    })
                  }}
                >
                  {save.isPending ? 'Salvando…' : 'Salvar a saída'}
                </Button>
              </div>
            </div>
          ) : null}
          {error === null ? null : (
            <InlineAlert tone="danger">{error}</InlineAlert>
          )}
        </div>
      ) : null}
    </Section>
  )
}

export const RotasPage = (): ReactNode => {
  const { allows } = useAuth()
  const canRoute = allows('delivery.route')
  const origin = useQuery({
    queryKey: ['delivery-routes', 'origin'],
    queryFn: async ({ signal }) => await fetchDeliveryOrigin(signal),
  })

  return (
    <Page>
      <PageHeader
        title="Rotas de entrega"
        description="Os pedidos da fila viram a rota de cada motoboy e da entrega a pé, com a ordem e o horário de cada parada."
      />
      {origin.data?.mapsReady === false ? (
        <InlineAlert tone="warning">
          O mapa ainda não foi ligado para esta conta. Peça ao suporte do Aeris
          para cadastrar a chave do mapa na Plataforma.
        </InlineAlert>
      ) : null}
      <Tabs defaultValue="montar">
        <TabsList aria-label="Rotas de entrega">
          <TabsTrigger value="montar">Montar saída</TabsTrigger>
          <TabsTrigger value="na-rua">Na rua</TabsTrigger>
          <TabsTrigger value="sem-ponto">Clientes sem ponto</TabsTrigger>
          <TabsTrigger value="entregadores">Entregadores</TabsTrigger>
          <TabsTrigger value="saida">Saída da loja</TabsTrigger>
        </TabsList>
        <TabsContent value="montar">
          {origin.data === undefined ? null : (
            <RoutePlanner
              origin={
                origin.data.origin === null
                  ? null
                  : {
                      latitude: origin.data.origin.latitude,
                      longitude: origin.data.origin.longitude,
                    }
              }
              canRoute={canRoute}
              canDispatch={canRoute && allows('pos.operate')}
            />
          )}
        </TabsContent>
        <TabsContent value="na-rua">
          <RoutesOnStreet canRoute={canRoute} />
        </TabsContent>
        <TabsContent value="sem-ponto">
          <UnlocatedTab canLocate={canRoute} />
        </TabsContent>
        <TabsContent value="entregadores">
          <CouriersEditor />
        </TabsContent>
        <TabsContent value="saida">
          <QueryState
            isPending={origin.isPending}
            isError={origin.isError}
            error={origin.error}
            onRetry={() => {
              void origin.refetch()
            }}
            what="a saída da loja"
            isEmpty={false}
            empty={null}
          >
            {origin.data === undefined ? null : (
              <OriginTab origin={origin.data} canEdit={canRoute} />
            )}
          </QueryState>
        </TabsContent>
      </Tabs>
    </Page>
  )
}
